<template>
  <v-dialog v-model="dialog" max-width="500" :persistent="loading" scrollable>
    <v-card>
      <v-card-title>
        {{ entry.type === "EXPENSE" ? "Registrar Pagamento" : "Registrar Recebimento" }}
      </v-card-title>
      <v-card-text>
        <p class="text-caption mb-4">{{ entry.description }}</p>
        <v-form v-model="valid" @submit.prevent>
          <v-text-field
            :value="entry.expectedAmount | currency"
            label="Valor previsto"
            outlined
            dense
            readonly
            :disabled="loading"
          />
          <v-text-field
            v-model="form.settledAmount"
            :label="entry.type === 'EXPENSE' ? 'Valor pago' : 'Valor recebido'"
            type="number"
            step="0.01"
            min="0"
            prefix="R$"
            outlined
            dense
            :rules="amountRules"
            :disabled="loading"
            @paste="onAmountPaste"
            @blur="onAmountBlur"
          />
          <v-alert
            v-if="difference !== 0"
            :type="difference > 0 ? 'success' : 'warning'"
            dense
            text
          >
            <span v-if="difference > 0">
              Juros/acréscimo: {{ difference | currency }}
            </span>
            <span v-else>
              Desconto: {{ Math.abs(difference) | currency }}
            </span>
          </v-alert>
          <v-text-field
            v-model="form.settledAt"
            :label="entry.type === 'EXPENSE' ? 'Data do pagamento' : 'Data do recebimento'"
            type="date"
            outlined
            dense
            hide-details
            :rules="[(v) => !!v || 'Data é obrigatória']"
            :disabled="loading"
          />
          <SettledDateChips
            v-model="form.settledAt"
            :due-date="entryDueDate"
            :disabled="loading"
          />
          <v-textarea
            v-model="form.notes"
            label="Observações"
            outlined
            dense
            rows="2"
            :disabled="loading"
          />
        </v-form>
        <v-alert v-if="error" type="error" dense text class="mt-2">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text small @click="close" :disabled="loading">Cancelar</v-btn>
        <v-btn
          color="primary"
          depressed
          small
          :loading="loading"
          :disabled="!valid"
          @click="submit"
        >
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import CASH from "@/services/ultimate/cash.js";
import { parseBrazilianCurrency } from "@/utils/parseCurrency";
import { withLocalDateISO } from "@/utils/dateOnlyToLocalISO";
import SettledDateChips from "@/components/ultimate/cash/SettledDateChips.vue";

export default {
  name: "SettleEntryModal",
  components: {
    SettledDateChips,
  },
  data() {
    return {
      dialog: false,
      loading: false,
      valid: false,
      error: null,
      entry: {},
      form: {
        settledAmount: null,
        settledAt: moment().format("YYYY-MM-DD"),
        notes: "",
      },
      amountRules: [
        (v) => v !== "" && v != null || "Valor é obrigatório",
        (v) => parseFloat(v) > 0 || "Valor deve ser maior que zero",
      ],
    };
  },
  computed: {
    difference() {
      const expected = parseFloat(this.entry.expectedAmount) || 0;
      const settled = parseFloat(this.form.settledAmount) || 0;
      return settled - expected;
    },
    entryDueDate() {
      if (!this.entry.dueDate) return null;
      return moment(this.entry.dueDate).format("YYYY-MM-DD");
    },
  },
  methods: {
    applyParsedAmount(rawValue) {
      const { amount } = parseBrazilianCurrency(rawValue);
      if (amount == null) return false;
      this.form.settledAmount = amount;
      return true;
    },
    onAmountPaste(event) {
      const text = event.clipboardData.getData("text");
      if (this.applyParsedAmount(text)) {
        event.preventDefault();
      }
    },
    onAmountBlur() {
      this.applyParsedAmount(this.form.settledAmount);
    },
    open(entry) {
      this.entry = entry;
      this.form = {
        settledAmount: entry.expectedAmount,
        settledAt: moment().format("YYYY-MM-DD"),
        notes: "",
      };
      this.error = null;
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    async submit() {
      try {
        this.loading = true;
        this.error = null;
        this.applyParsedAmount(this.form.settledAmount);
        await CASH.settle(this.entry.id, withLocalDateISO(this.form, ["settledAt"]));
        this.$emit("settled");
        this.close();
      } catch (error) {
        this.error = error.message || "Erro ao registrar recebimento";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.$parent.$on("cash-entry-settle", this.open);
  },
  beforeDestroy() {
    this.$parent.$off("cash-entry-settle", this.open);
  },
};
</script>
